// Shared hide-review dialog. Admin-only usage; the caller is responsible
// for only rendering the entrypoint for admins (the endpoint also 403s).
// Derived values declared before the hooks/handlers that use them.
import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

export const HIDE_REASONS = [
  { key: "duplicate", label: "Duplicate review (your original review is still live)" },
  { key: "not_about_trail", label: "Not about this trail" },
  { key: "inappropriate", label: "Inappropriate or offensive language" },
  { key: "personal_info", label: "Contains personal information" },
  { key: "spam", label: "Spam or advertising" },
  { key: "out_of_date", label: "Out of date or inaccurate" },
  { key: "other", label: "Other" },
];

export default function HideReviewDialog({ open, onClose, review, locationId, onHidden }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const noteRequired = reason === "other";
  const canSubmit = Boolean(reason) && !busy && (!noteRequired || note.trim().length > 0);

  const close = () => {
    if (busy) return;
    setReason("");
    setNote("");
    onClose?.();
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post(
        `/locations/${locationId}/reviews/${review.review_id}/admin-hide`,
        { reason, note: note.trim() },
      );
      toast.success("Review hidden. The walker has been emailed.");
      onHidden?.();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't hide review");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 p-4"
      data-testid="hide-review-modal"
      onClick={close}
    >
      <div className="td-card max-w-md w-full p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="td-section-label">Hide review</div>
        <p className="text-sm text-brand-subtle mt-1">
          The walker will be emailed the reason you pick. They can post a new review afterwards.
        </p>
        <fieldset className="mt-3 space-y-2" aria-label="Reason for hiding">
          {HIDE_REASONS.map((r) => (
            <label
              key={r.key}
              className="flex items-start gap-2 text-sm cursor-pointer"
              data-testid={`hide-reason-${r.key}`}
            >
              <input
                type="radio"
                name="hide-reason"
                value={r.key}
                checked={reason === r.key}
                onChange={() => setReason(r.key)}
                className="mt-1 accent-brand-primary"
              />
              <span>{r.label}</span>
            </label>
          ))}
        </fieldset>
        <label className="block mt-3 text-xs text-brand-subtle">
          Note {noteRequired ? "(required for 'Other')" : "(optional)"} — max 200 chars
        </label>
        <textarea
          data-testid="hide-review-note"
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 200))}
          className="td-input mt-1"
          rows={2}
          maxLength={200}
        />
        <div className="flex gap-2 justify-end mt-4">
          <button
            type="button"
            data-testid="hide-review-cancel"
            onClick={close}
            disabled={busy}
            className="td-btn-secondary !py-2 !px-4 !text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="hide-review-confirm"
            onClick={submit}
            disabled={!canSubmit}
            className="td-btn-primary !py-2 !px-4 !text-sm disabled:opacity-50"
          >
            {busy ? "Hiding…" : "Hide review"}
          </button>
        </div>
      </div>
    </div>
  );
}
